import type {
  BorrowTermSheet,
  FeeTerm,
  MaturityTerms,
  SupplyExitRoute,
  SupplyTermSheet,
  TermSheet,
} from './types'

/**
 * Deterministic text generation — headlines and descriptions are templated
 * FROM the structured fields, so prose can never drift from the numbers.
 * Nothing here is hand-written per lender.
 */

export const SECS_PER_DAY = 86_400
export const SECS_PER_YEAR = 31_536_000

/** `4.1234` → `"4.12 %"`; trims to 2dp, drops a trailing `.00`. */
/**
 * Format an already-PERCENT value (`49.88` → `"49.88%"`).
 *
 * Never pass a fraction. The convention across the term sheet is **rates are
 * percent, factors/ratios are fractions**, so `liquidationLtv`, `penalty`,
 * `utilization` and every `*Ratio` must be multiplied by 100 at the call site.
 *
 * Trailing zeros are trimmed only inside the FRACTIONAL part: a naive
 * `/\.?0+$/` strip eats integer zeros whenever `dp = 0` leaves no decimal
 * point, turning `100` into `"1"` and `1000` into `"1"`.
 *
 * No space before the `%` — the portal formats the same numbers without one,
 * and a headline reading "49.88 %" next to a table cell reading "49.88%" looks
 * like two different figures.
 */
export function pct(value: number | undefined, dp = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const fixed = value.toFixed(dp)
  const trimmed = fixed.includes('.')
    ? fixed.replace(/0+$/, '').replace(/\.$/, '')
    : fixed
  return `${trimmed === '' || trimmed === '-' ? '0' : trimmed}%`
}

/** Duration in seconds → the coarsest human unit that stays honest. */
export function duration(secs: number | undefined): string {
  if (secs == null || !Number.isFinite(secs) || secs < 0) return '—'
  if (secs === 0) return 'none'
  if (secs >= SECS_PER_DAY) {
    const d = secs / SECS_PER_DAY
    return `${d % 1 === 0 ? d : d.toFixed(1)} day${d === 1 ? '' : 's'}`
  }
  if (secs >= 3_600) {
    const h = secs / 3_600
    return `${h % 1 === 0 ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}`
  }
  if (secs >= 60) {
    const m = Math.round(secs / 60)
    return `${m} minute${m === 1 ? '' : 's'}`
  }
  return `${Math.round(secs)} second${Math.round(secs) === 1 ? '' : 's'}`
}

/** Unix seconds → `"3 Sep 2026"`. Locale-independent so snapshots are stable. */

export function shortDate(unixSecs: number | undefined): string {
  if (unixSecs == null || !Number.isFinite(unixSecs)) return '—'
  const d = new Date(unixSecs * 1000)
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** One fee → a self-contained phrase, correct even for an unrecognised `id`. */
export function feePhrase(fee: FeeTerm): string {
  const magnitude = Math.abs(fee.value)
  let amount: string
  switch (fee.unit) {
    case 'bps':
      amount = `${magnitude} bps`
      break
    case 'apr-percent':
      amount = `${pct(magnitude)}/yr`
      break
    case 'percent':
      amount = pct(magnitude)
      break
    default:
      amount = String(magnitude)
  }
  const rebate = fee.value < 0 ? ' rebate' : ''
  const qualifier = fee.indicative ? ' (estimated, resolved at execution)' : ''
  // A governance-mutable fee reads as unlimited unless its ceiling is stated.
  const bound =
    fee.mutable && fee.cap != null
      ? `, governance-set up to a maximum of ${
          fee.unit === 'bps' ? `${fee.cap} bps` : pct(fee.cap)
        }`
      : fee.mutable
        ? ', governance-set'
        : ''
  return `${fee.label}: ${amount}${rebate}${bound}${qualifier}`
}

/** Has a dated term already passed? Against the CLOCK, never a stored flag. */
function hasMatured(m: MaturityTerms, nowSecs?: number): boolean {
  if (m.kind !== 'fixed-date' || !m.maturity) return false
  return m.maturity <= (nowSecs ?? Math.floor(Date.now() / 1000))
}

function maturityPhrase(m: MaturityTerms): string {
  // Past tense once the date is behind us. "until 27 Aug 2026" read on 9 Sep
  // as a term still running, on a sheet whose every other clause had already
  // switched to the matured branch — the countdown, the gating, the CTA.
  if (m.kind === 'fixed-date' && m.maturity)
    return `${hasMatured(m) ? 'matured' : 'until'} ${shortDate(m.maturity)}`
  if (m.kind === 'rolling-duration') {
    if (m.maxDurationSecs) return `for up to ${duration(m.maxDurationSecs)}`
    return 'for a term you choose'
  }
  return 'with no maturity'
}

function rateLabel(sheet: SupplyTermSheet | BorrowTermSheet): string {
  switch (sheet.rate.kind) {
    case 'fixed-term':
    case 'fixed-open':
      return 'Fixed'
    case 'user-set':
      return 'You set'
    case 'zero-interest':
      return 'No interest'
    case 'prepaid':
      return 'Prepaid'
    case 'nav-accrual':
      return 'NAV'
    case 'realized':
      // A trading book's number is an OUTCOME, not a rate anything sets.
      // Labelling it "Variable" would imply a mechanism that does not exist.
      return 'Realized'
    case 'none':
      return 'No yield'
    default:
      return 'Variable'
  }
}

/**
 * How the rate behaves from here — the clause after the number.
 *
 * Was a bare `isLocked ? 'locked for the term' : 'floating with utilization'`,
 * which is right for a pool market and wrong for everything else: a savings
 * vault's rate is set by governance and moves for reasons no curve explains, a
 * NAV share price is published rather than earned, and a trading vault's number
 * is a measurement of the past. Keyed on `rate.kind` so each says its own truth.
 */
function rateBehaviour(rate: SupplyTermSheet['rate']): string {
  switch (rate.kind) {
    case 'fixed-term':
    case 'fixed-open':
      return 'locked for the term'
    case 'variable-managed':
      return 'set by the protocol and subject to change'
    case 'nav-accrual':
      return 'tracking a net asset value published by the operator'
    case 'realized':
    case 'none':
      return 'measured over a past window — a result, not a promise'
    case 'user-set':
      return 'the rate you chose'
    default:
      return 'floating with utilization'
  }
}

/** ` (trailing weekly)` — only where the number is a measurement. */
function windowNote(rate: SupplyTermSheet['rate']): string {
  const w = rate.window
  if (!w || w.kind !== 'trailing') return ''
  return w.label ? ` (trailing ${w.label})` : ' (trailing)'
}

const earlyRepayPhrase: Record<string, string> = {
  free: 'repay any time at no extra cost',
  discount: 'repaying early earns a rebate',
  penalty: 'repaying early costs a penalty',
  'market-price': 'exit early only at the market price',
  'not-allowed': 'no early repayment',
}

const exitPhrase: Record<string, string> = {
  instant: 'withdraw any time',
  'instant-capped': 'withdraw up to available liquidity',
  'instant-or-queued': 'withdraw instantly or queue',
  'fee-or-queued': 'instant exit for a fee, or queue for free',
  'fixed-cooldown': 'withdraw after a cooldown',
  queued: 'withdrawals are queued',
  'request-based': 'withdrawals need a request',
  'market-sale': 'exit by selling on the market',
  'at-maturity': 'locked until maturity',
  'off-chain': 'exit off-chain',
  'dex-only': 'exit only via a DEX',
}

/** Supply-side headline: ≤ ~100 chars, always populated. */
/**
 * Where the headline yield actually comes from.
 *
 * `aprTotal` is `base + rewards + intrinsic`, and a single percentage cannot
 * say which. That matters most exactly where it is least visible: a
 * yield-bearing collateral (savUSD, sUSDe, an LST) can show a healthy number
 * while the market itself pays nothing — you would earn the same holding the
 * token, and you are taking this market's risk for the difference.
 *
 * It is decisive when looping: intrinsic yield accrues on the ASSET, so it
 * scales with the position, while a lending spread scales with leverage — two
 * different carry profiles behind one number.
 *
 * Rendered only when there IS an intrinsic component, so ordinary pool markets
 * read exactly as before.
 */
function provenance(rate: SupplyTermSheet['rate']): string {
  const total = rate.aprTotal
  const base = rate.components?.base ?? 0
  const intrinsic = rate.components?.intrinsic ?? 0
  if (!(intrinsic > 0) || !(total > 0)) return ''
  // The whole thing is the asset's own yield — say so plainly rather than
  // implying the market pays it.
  if (base <= 0.0001) return ' (all from the asset itself)'
  if (intrinsic >= total / 2) {
    return ` (${pct(intrinsic)} from the asset, ${pct(base)} from the market)`
  }
  return ` (incl. ${pct(intrinsic)} from the asset)`
}

/**
 * One leg of an exit, as a phrase: `instantly for 0.50 %`,
 * `free after 7 days (min 50.00)`.
 *
 * Every clause is conditional because every clause is genuinely optional
 * across our providers — a queue with no pinned wait, a leg with no published
 * fee, a leg with no floor. A `feeBps` of 0 is a POSITIVE claim (free) and is
 * rendered; an absent one says nothing rather than implying free.
 */
function routePhrase(r: SupplyExitRoute, symbol?: string): string {
  const when =
    r.kind === 'instant'
      ? 'instantly'
      : r.waitSeconds
        ? `after ${duration(r.waitSeconds)}`
        : r.kind === 'market'
          ? 'by selling it'
          : 'after a wait'
  const cost =
    r.feeBps === 0
      ? ' free'
      : r.feeBps != null
        ? ` for ${(r.feeBps / 100).toFixed(2)} %`
        : r.feeUnknown
          ? ' for a fee'
          : ''
  const floor =
    r.minAmountFormatted != null && r.minAmountFormatted > 0
      ? ` (min ${amount(r.minAmountFormatted)}${symbol ? ` ${symbol}` : ''})`
      : ''
  // A zero buffer is not a small one — "up to 0.00" reads as a rounding
  // artefact when it means the leg cannot serve anything at all.
  const cap =
    r.kind !== 'instant' || r.capacityFormatted == null
      ? ''
      : r.capacityFormatted <= 0
        ? ', though nothing is available right now'
        : `, up to ${amount(r.capacityFormatted)}${symbol ? ` ${symbol}` : ''} right now`
  return `${when}${cost}${floor}${cap}`
}

/**
 * The exit sentence for a multi-leg exit.
 *
 * This is the case the single-mode prose got WRONG rather than merely
 * incomplete: for `fee-or-queued` it printed only "getting out takes 7 days —
 * request first, then claim", which describes the leg most holders cannot
 * use, and never mentioned that there is an immediate exit or what it costs.
 * Spelling out both legs is the whole point of `SupplyExitTerms.routes`.
 */
function routesSentence(routes: SupplyExitRoute[], symbol?: string): string {
  const legs = routes.map((r) => routePhrase(r, symbol))
  const body =
    legs.length === 1
      ? legs[0]
      : `${legs.slice(0, -1).join(', ')}, or ${legs[legs.length - 1]}`
  return `You can leave ${body}.`
}

/** Human amount with a sensible number of digits for a token quantity. */
function amount(v: number): string {
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 })
  // Trailing zeros make a round floor look like a measured quantity — a
  // minimum of exactly 50 should read "50", not "50.00".
  if (v >= 1) return v.toFixed(2).replace(/\.?0+$/, '')
  return v.toPrecision(3)
}

/**
 * Headline exit clause built from the ROUTES rather than the mode name.
 *
 * `exitPhrase['fee-or-queued']` reads "instant exit for a fee, or queue for
 * free" — a sentence that is wrong on both counts for Treehouse, whose queue
 * charges 5 bps and refuses anything under 50 wstETH. The mode name cannot
 * know that; the routes do. Falls back to the static phrase whenever the row
 * publishes fewer than two legs, so nothing else changes.
 */
function headlineExitFromRoutes(
  routes: SupplyExitRoute[] | undefined,
  symbol?: string,
): string | undefined {
  if (!routes || routes.length < 2) return undefined
  const fast = routes.find((r) => r.kind === 'instant')
  const slow = routes.find((r) => r.kind === 'queued')
  if (!fast || !slow) return undefined

  const fastPart =
    fast.feeBps == null
      ? 'instant exit'
      : fast.feeBps === 0
        ? 'free instant exit'
        : `instant exit for ${(fast.feeBps / 100).toFixed(2)} %`
  const slowCost =
    slow.feeBps === 0
      ? 'free'
      : slow.feeBps != null
        ? `${(slow.feeBps / 100).toFixed(2)} %`
        : undefined
  // The floor is the reason the cheap leg may not be an option at all, so it
  // earns its place in the one-line summary.
  const floor =
    slow.minAmountFormatted != null && slow.minAmountFormatted > 0
      ? ` above ${amount(slow.minAmountFormatted)}${symbol ? ` ${symbol}` : ''}`
      : ''
  const slowPart = slowCost ? `queue${floor} for ${slowCost}` : `queue${floor}`
  return `${fastPart}, or ${slowPart}`
}

export function supplyHeadline(
  s: SupplyTermSheet,
  /** Underlying asset, so a route minimum in the headline names its unit. */
  sheet: Pick<TermSheet, 'asset'> = {},
): string {
  if (s.role === 'collateral') {
    return `Collateral only · ${maturityPhrase(s.maturity)}`
  }
  const rate = `${rateLabel(s)} ${pct(s.rate.aprTotal)}${windowNote(s.rate)}${provenance(s.rate)}`
  const exit =
    headlineExitFromRoutes(s.exit.routes, sheet.asset?.symbol) ??
    exitPhrase[String(s.exit.mode)] ??
    (s.exit.settlement === 'sync' ? 'withdraw any time' : 'delayed withdrawal')
  const cooldown = s.exit.cooldownSecs
    ? ` (${duration(s.exit.cooldownSecs)})`
    : ''
  // A matured term LEADS with the fact, and quotes no rate at all. The rate is
  // zero by then (`earnRateAtMaturity`), and "Fixed 0.00% matured 27 Aug 2026"
  // states the same thing twice while reading like a broken number. What the
  // holder still needs is the way out, so that is what remains.
  if (hasMatured(s.maturity)) {
    return `Matured ${shortDate(s.maturity.maturity)} · ${exit}${cooldown}`
  }
  const mat =
    s.maturity.kind === 'perpetual' ? '' : ` ${maturityPhrase(s.maturity)}`
  // The warm-up goes BETWEEN the rate and the exit, because that is exactly
  // where the two would otherwise contradict each other: "Variable 3.5 % ·
  // withdraw any time" is two true halves that together imply you earn from
  // day one and can leave whenever, when a stay shorter than the warm-up
  // realises ZERO. Naming the delay is what makes the pair honest.
  const warmup = s.rate.warmupSecs
    ? ` · earns after ${duration(s.rate.warmupSecs)}`
    : ''
  return `${rate}${mat}${warmup} · ${exit}${cooldown}`
}

/** Borrow-side headline. */
export function borrowHeadline(b: BorrowTermSheet): string {
  const rate =
    b.rate.kind === 'zero-interest'
      ? 'No ongoing interest'
      : b.rate.kind === 'user-set'
        ? // Only quote the band when BOTH bounds are known. Printing
          // "You set the rate (——)" is worse than saying nothing: it looks
          // like a broken number rather than a missing one.
          b.rate.minApr != null && b.rate.maxApr != null
          ? `You set the rate (${pct(b.rate.minApr)}–${pct(b.rate.maxApr)})`
          : 'You set the rate'
        : `${rateLabel(b)} ${pct(b.rate.apr)}`
  const mat =
    b.maturity.kind === 'perpetual' ? '' : ` ${maturityPhrase(b.maturity)}`
  const exit = earlyRepayPhrase[String(b.exit.earlyRepay)] ?? 'see repay terms'
  // A repay LOCK overrides the early-repay phrase rather than sitting next to
  // it. `earlyRepay: 'free'` renders as "repay any time at no extra cost",
  // which is not merely incomplete while a hold period runs — it is FALSE, and
  // it is false about the exact thing a borrower would act on. Lead with the
  // lock and keep the cost phrase after it, since both are true in sequence.
  if (b.exit.cooldownSecs) {
    return `${rate}${mat} · no repayment for ${duration(b.exit.cooldownSecs)}, then ${exit}`
  }
  return `${rate}${mat} · ${exit}`
}

/** Supply-side description — 1–3 sentences, market values interpolated. */
export function supplyDescription(
  s: SupplyTermSheet,
  // `asset` joins `utilization` here so an exit-route sentence can name the
  // unit its minimum and capacity are denominated in — "50 wstETH or more"
  // rather than a bare "50", which reads as shares.
  sheet: Pick<TermSheet, 'utilization' | 'asset'> = {},
): string {
  const parts: string[] = []

  if (s.role === 'collateral') {
    parts.push(
      'Deposits here are collateral only — they secure borrowing and do not earn interest.',
    )
  } else {
    const comp: string[] = [`${pct(s.rate.components.base)} base`]
    if (s.rate.components.rewards)
      comp.push(`${pct(s.rate.components.rewards)} rewards`)
    if (s.rate.components.intrinsic)
      comp.push(`${pct(s.rate.components.intrinsic)} from the asset itself`)
    parts.push(
      `You earn ${pct(s.rate.aprTotal)} (${comp.join(' + ')}), ${rateBehaviour(s.rate)}.`,
    )
  }

  // A multi-leg exit is described leg by leg — the flat branches below cannot
  // express two legs and, for `fee-or-queued`, actively mislead by naming only
  // the queued one.
  if (s.exit.routes && s.exit.routes.length > 1) {
    parts.push(routesSentence(s.exit.routes, sheet.asset?.symbol))
  } else if (s.exit.mode === 'instant') {
    const util = sheet.utilization?.utilization
    parts.push(
      util != null && util >= 0.95
        ? `Withdrawals are instant in principle, but utilization is ${pct(util * 100)} — there may be little or no liquidity to withdraw right now.`
        : 'Withdrawals are instant, up to the liquidity available in the market.',
    )
  } else if (s.exit.mode === 'instant-capped') {
    // The cap is the whole point of this mode, so quote it. A bare "instant"
    // sentence here would describe a withdrawal the vault cannot always settle.
    const ratio = s.exit.liquidity?.ratio
    parts.push(
      ratio != null
        ? `Withdrawals settle in the same block, but only up to the ${pct(ratio * 100)} of the vault that is liquid right now — the rest has to wait for the position to free up.`
        : 'Withdrawals settle in the same block, but only up to the portion of the vault held liquid — a larger exit has to wait.',
    )
  } else if (s.exit.cooldownSecs) {
    parts.push(
      `Getting out takes ${duration(s.exit.cooldownSecs)} — request first, then claim.`,
    )
  } else if (s.exit.priceRisk === 'market-price') {
    parts.push(
      'There is no redemption before maturity; exiting early means selling the position on a market at whatever price is bid.',
    )
  } else if (s.exit.priceRisk === 'may-be-impossible') {
    parts.push(
      'Exiting early requires a buyer on the order book, and there may not be one at any price.',
    )
  }

  if (s.backedBy && s.backedBy.count > 0) {
    parts.push(
      s.backedBy.weightBasis === 'unweighted'
        ? `Your deposit is lent against ${s.backedBy.count} accepted collateral asset${s.backedBy.count === 1 ? '' : 's'}; the protocol does not record which one backs which borrow.`
        : `Your deposit is lent against ${s.backedBy.count} collateral market${s.backedBy.count === 1 ? '' : 's'}${
            s.backedBy.topWeightPct != null
              ? `, the largest being ${s.backedBy.topWeightPct.toFixed(0)} % of the total`
              : ''
          }.`,
    )
  }

  return parts.join(' ')
}

/** Borrow-side description. */
export function borrowDescription(b: BorrowTermSheet): string {
  const parts: string[] = []

  if (b.rate.kind === 'zero-interest') {
    parts.push(
      'This market charges no ongoing interest — the borrowing cost is a one-off fee taken at open.',
    )
  } else if (b.rate.kind === 'prepaid') {
    parts.push(
      'Interest is prepaid by holding a separate token; the debt principal itself does not grow.',
    )
  } else if (b.rate.kind === 'user-set') {
    parts.push(
      b.rate.minApr != null && b.rate.maxApr != null
        ? `You choose your own rate between ${pct(b.rate.minApr)} and ${pct(b.rate.maxApr)}; a lower rate is cheaper but makes you redeemed first.`
        : 'You choose your own interest rate; a lower rate is cheaper but makes you redeemed first.',
    )
  } else {
    // "Floating with utilization" is only true of an IRM. `variable-managed`
    // is the explicit OPPOSITE — a rate somebody sets, with no curve behind it
    // — and saying otherwise invites a borrower to wait for utilization to
    // fall, which on Frankencoin (a per-position `annualInterestPPM`) or Sky
    // (a governance stability fee) will never happen.
    const how = b.rate.isLocked
      ? ', locked for the term'
      : b.rate.kind === 'variable-curve'
        ? ', floating with utilization'
        : b.rate.kind === 'variable-managed'
          ? ', set by the protocol rather than floating with utilization'
          : ''
    parts.push(`You pay ${pct(b.rate.apr)}${how}.`)
  }

  parts.push(
    b.debtShape === 'static-face'
      ? 'The amount owed is fixed at trade time and does not accrue.'
      : b.debtShape === 'prepaid'
        ? 'The debt principal is static; the cost is the prepaid interest token burning down.'
        : 'The amount owed grows continuously as interest accrues.',
  )

  if (b.maturity.kind === 'fixed-date' && b.maturity.maturity) {
    const consequence: Record<string, string> = {
      'penalty-accrues': 'a late penalty starts accruing',
      liquidatable: 'the loan can be liquidated regardless of its health',
      'default-seizure': 'the loan defaults and the collateral is forfeit',
      'physical-delivery': 'the collateral is delivered to lenders',
      refinanced: 'a keeper refinances it into the variable position',
      'stops-earning': 'it simply stops accruing',
    }
    const what = consequence[String(b.maturity.atMaturity)]
    parts.push(
      `It matures on ${shortDate(b.maturity.maturity)}${what ? `; if nothing is done then, ${what}` : ''}.`,
    )
  }

  // WHAT YOU RECEIVE vs WHAT YOU OWE.
  //
  // Entry charges levied on the principal are deducted from the payout, so the
  // debt is larger than the money that arrives. Every other clause in this
  // description is about the debt; without this one a borrower reads the debt
  // figure as the proceeds, and on Frankencoin that is wrong by up to 38 %
  // (a 37 % equity-reserve contribution plus ~1 % upfront interest).
  //
  // `fees` carried these all along and carried them EXACTLY — but only as
  // structured rows, which a caller has to know to render. Prose is templated
  // from the structured fields precisely so the sentence cannot disagree with
  // the numbers beside it; silently omitting the largest of them broke that in
  // the one direction that flatters the market.
  //
  // Rebates (negative values) are excluded: a repay-time credit is not
  // withheld at open, and netting it here would understate the gap.
  const withheld = (b.fees ?? []).filter(
    (f) =>
      f.when === 'entry' &&
      f.basis === 'principal' &&
      f.unit === 'percent' &&
      f.value > 0,
  )
  if (withheld.length > 0) {
    const total = withheld.reduce((a, f) => a + f.value, 0)
    const named = withheld
      .map((f) => `${f.label.toLowerCase()} ${pct(f.value)}`)
      .join(' + ')
    parts.push(
      `${pct(total)} of anything you borrow is withheld at open` +
        (withheld.length > 1 ? ` (${named})` : '') +
        `, so you receive ${pct(100 - total)} of what you owe.`,
    )
  }

  if (b.liquidation.trigger === 'time') {
    parts.push(
      'Liquidation here is triggered by TIME, not price — being late is the trigger, and being over-collateralised does not protect you.',
    )
  } else if (
    b.liquidation.model === 'auction' &&
    !(b.liquidation.liquidationLtv! > 0)
  ) {
    // An auction market has no LTV threshold to quote, and printing the
    // absent one as `0` stated a trigger that does not exist — on the market
    // whose entire distinguishing feature is that liquidation is a public
    // auction against an owner-declared price, decided by whether the
    // collateral still sells, not by how much is owed.
    const pen = b.liquidation.penalty
    const win = b.liquidation.windowSecs
    parts.push(
      'Liquidation is a public auction rather than a loan-to-value threshold' +
        (win ? `, open for ${duration(win)} once started` : '') +
        (pen != null ? `, and the liquidator is paid ${pct(pen * 100)}` : '') +
        '.',
    )
  } else if (b.liquidation.model === 'repay-to-target-hf') {
    // Aave V4. Stating an LTV threshold here is right — V4 has one — but
    // stopping there would let the reader carry over the close-factor
    // assumption every other pool trains them on. The share of the debt taken
    // is an OUTCOME of how far the position fell, so the sentence has to name
    // the sizing rule instead of a number, and mark the penalty as a ceiling.
    const l = b.liquidation
    const start =
      l.liquidationLtv != null && l.liquidationLtv > 0
        ? `Liquidation starts at ${pct(l.liquidationLtv * 100)} LTV and`
        : 'A liquidation'
    const sized =
      l.targetHealthFactor != null
        ? ` repays as much of your debt as it takes to restore a health factor of ${l.targetHealthFactor.toFixed(2)}`
        : ' repays as much of your debt as it takes to restore the spoke\u2019s target health factor'
    // `> 0`, not `!= null`: on a V4 spoke a penalty of exactly 0 means the row
    // is not usable as collateral there (the config is disabled and the
    // converter emits 0), never that a liquidator works for free. The same
    // guard is why the generic branch below tests `liquidationLtv > 0`.
    const bonus =
      l.penalty != null && l.penalty > 0
        ? ` — the liquidator\u2019s bonus scales up to ${pct(l.penalty * 100)}` +
          (l.healthFactorForMaxBonus != null
            ? ` at a health factor of ${l.healthFactorForMaxBonus.toFixed(2)}`
            : '') +
          '.'
        : '.'
    parts.push(`${start}${sized} rather than a fixed share of it${bonus}`)
  } else if (
    b.liquidation.liquidationLtv != null &&
    b.liquidation.liquidationLtv > 0
  ) {
    // A threshold of 0 is not a threshold — it would mean every position is
    // liquidatable on open. It means the sheet does not have the number, and
    // an omitted clause reads as "not stated" while a 0 % one reads as a fact.
    const pen = b.liquidation.penalty
    parts.push(
      `Liquidation starts at ${pct(b.liquidation.liquidationLtv * 100)} LTV` +
        (pen != null ? `, with a ${pct(pen * 100)} penalty` : '') +
        '.',
    )
  }

  return parts.join(' ')
}
